import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

const wrap = (n) => (n < 0 ? `(${n})` : `${n}`);

/* ========= Generator ========= */
export function generateFindBTask() {
  const ax = Math.floor(Math.random() * 21) - 10;
  const ay = Math.floor(Math.random() * 21) - 10;
  const bx = Math.floor(Math.random() * 21) - 10;
  const by = Math.floor(Math.random() * 21) - 10;

  const midpoint = [(ax + bx) / 2, (ay + by) / 2];

  return {
    type: "FindBTask",
    data: { a: [ax, ay], b: [bx, by], midpoint },
    answer: [bx, by]
  };
}

export function generateTask() {
  return generateFindBTask();
}

/* ========= Treść ========= */
export function renderTask(task) {
  const { a, midpoint } = task.data;

  return (
    <p>
      W odcinku <InlineMath math={'\\overline{AB}'} /> dany jest punkt{" "}
      <InlineMath math={`A = \\left(${formatMixedFractionLatex(a[0])},\\ ${formatMixedFractionLatex(a[1])}\\right)`} />{" "}
      oraz środek odcinka{" "}
      <InlineMath math={`S = \\left(${formatMixedFractionLatex(midpoint[0])},\\ ${formatMixedFractionLatex(midpoint[1])}\\right)`} />.
      Wyznacz współrzędne punktu <InlineMath math={'B'} />.
    </p>
  );
}

/* ========= Input (2× FractionInput) ========= */
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_B ='} /></span>
        <FractionInput
          value={userInput?.x || ""}
          onChange={(val) => onChange({ ...userInput, x: val })}
          disabled={disabled}
          label={"x_B"}
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'y_B ='} /></span>
        <FractionInput
          value={userInput?.y || ""}
          onChange={(val) => onChange({ ...userInput, y: val })}
          disabled={disabled}
          label={"y_B"}
        />
      </div>
    </div>
  );
}

/* ========= Checking (1 pkt za całość) ========= */
export function checkAnswer(task, userInput) {
  const x = userInput?.x?.parsed;
  const y = userInput?.y?.parsed;

  if ([x, y].some(v => v == null || isNaN(v))) return false;

  const [bx, by] = task.answer;
  return Math.abs(x - bx) < 0.01 && Math.abs(y - by) < 0.01;
}

/* ========= Odpowiedź ========= */
export function renderAnswer(task) {
  const [bx, by] = task.answer;
  return <InlineMath math={`B = \\left(${formatMixedFractionLatex(bx)},\\ ${formatMixedFractionLatex(by)}\\right)`} />;
}

/* ========= Wyjaśnienie ========= */
export function renderExplanation({ data, answer }) {
  if (!data || !answer) return <div style={{ color: "red" }}>Brak danych do wyjaśnienia.</div>;

  const [ax, ay] = data.a;
  const [sx, sy] = data.midpoint;
  const [bx, by] = answer;

  return (
    <div>
      <p><strong>Wyjaśnienie krok po kroku:</strong></p>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 1.</strong> Korzystamy ze wzoru na środek odcinka:</p>
        <BlockMath math={'S = \\left( \\frac{x_A + x_B}{2},\\ \\frac{y_A + y_B}{2} \\right)'} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <p><strong>Krok 2.</strong> Układamy równania:</p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1rem",
            alignItems: "start",
          }}
        >
          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'x_B'} /></div>
            <BlockMath math={`\\frac{${wrap(ax)} + x_B}{2} = ${formatMixedFractionLatex(sx)}`} />
            <BlockMath math={`x_B = ${formatMixedFractionLatex(2 * sx)} ${ax < 0 ? "+" : "-"} ${Math.abs(ax)}`} />
            <BlockMath math={`x_B = ${formatMixedFractionLatex(bx)}`} />
          </div>

          <div>
            <div style={{ textAlign: "center", fontWeight: 600 }}><InlineMath math={'y_B'} /></div>
            <BlockMath math={`\\frac{${wrap(ay)} + y_B}{2} = ${formatMixedFractionLatex(sy)}`} />
            <BlockMath math={`y_B = ${formatMixedFractionLatex(2 * sy)} ${ay < 0 ? "+" : "-"} ${Math.abs(ay)}`} />
            <BlockMath math={`y_B = ${formatMixedFractionLatex(by)}`} />
          </div>
        </div>
      </div>

      <p><strong>Odpowiedź:</strong></p>
      <BlockMath math={`B = \\left(${formatMixedFractionLatex(bx)},\\ ${formatMixedFractionLatex(by)}\\right)`} />
    </div>
  );
}

/* ========= User answer preview ========= */
export function renderUserAnswer(userInput) {
  if (!userInput?.x?.raw || !userInput?.y?.raw) return <i>(brak odpowiedzi)</i>;

  const x = formatLatexPreview(userInput.x.raw, "x_B");
  const y = formatLatexPreview(userInput.y.raw, "y_B");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x} /></div>
      <div style={{ marginTop: "0.5rem" }}><InlineMath math={y} /></div>
    </div>
  );
}
